import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import {assign, find, flatMap, forEach, map, size, uniq, values} from 'lodash';
import PropTypes from 'prop-types';
import {useState} from 'react';

import Button from 'components/Button';
import GenotypePickerForm from 'components/GenotypePicker/form';
import RecommendationAnnotation from 'components/RecommendationAnnotation';
import InfoLink from 'components/links/Info';
import Link from 'components/links/Link';
import FactSection from 'components/shared/fact_section';

import 'components/GenotypePicker/index.scss';

const propTypes = {
  annotationId: PropTypes.string.isRequired,
  note: PropTypes.element,
  source: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.shape({
    genesymbol: PropTypes.string,
    displaySingleAllele: PropTypes.bool,
    allowSingleAllele: PropTypes.bool,
    alleles: PropTypes.arrayOf(PropTypes.string),
    phenotypeOptionMap: PropTypes.object,
  })),
  chemicalId: PropTypes.string,
  gsiResults: PropTypes.object,
  gsiQuery: PropTypes.object,
};
/**
 * Displays fields for the user to pick a specific genotype which is then used to display specific recommendations.
 *
 * @param {object} props - props container
 * @param {string} props.annotationId - accession ID of the "parent" annotation
 * @param {*} props.note - optional note to display below picker
 * @param {string} props.source - optional data source string for source-specific content
 * @param {Array} props.options - an array of option data, one per gene
 * @param {string} props.chemicalId - optional, the specific chemical to show the recommendation for
 * @param {object} props.gsiResults - optional, results from a GSI query
 * @param {object} props.gsiQuery - optional, the chosen values from a GSI query in a map of gene symbol to 3-value array
 */
export default function GenotypePicker({annotationId, note, source,
  chemicalId, gsiResults, gsiQuery, options = []}) {
  const [drugs, setDrugs] = useState(!gsiResults ? [] : map(values(gsiResults), (r) => r[source.toLowerCase()]));

  const initFormValues = {};
  forEach(options, (o) => {
    initFormValues[o.genesymbol] = ['', '', ''];
  });
  assign(initFormValues, gsiQuery);

  const handleSuccess = (results) => {
    setDrugs(results);
  };

  const handleClear = () => {
    setDrugs([]);
  };

  const pickerTitle = size(drugs) > 0
    ? 'Genotype-specific results'
    : 'Specify a genotype or phenotype for specific annotations';

  return (
    <FactSection headerClasses="mb-1" title={pickerTitle} id="tour-annotation-picker">
      <div className={clsx({'d-none': size(drugs) > 0}, 'mt-3')}>
        <GenotypePickerForm options={options} annotationId={annotationId} chemicalId={chemicalId} values={initFormValues} onSuccess={handleSuccess} className="mb-2" />
        {note}
      </div>
      <PickerResults annotationId={annotationId} drugs={drugs} query={initFormValues} onClear={handleClear} />
      <small className="genotype-specific-annotations-picker_notes">
        <GeneralWarning />
        <DpwgWarning source={source} />
      </small>
    </FactSection>
  );
}
GenotypePicker.propTypes = propTypes;

function PickerResults({drugs, query, annotationId, onClear}) {
  const resultGuideline = find(
    flatMap(values(drugs), (d) => d.annotations),
    (g) => g?.id === annotationId,
  );
  if (!resultGuideline) {
    return null;
  }

  let recommendationsToShow;
  const {genotypes, hasNoRecommendation, phenotypes: resultPhenotypes, recommendationAnnotations} = resultGuideline;
  if (hasNoRecommendation) {
    recommendationsToShow = <RecommendationAnnotation phenotypes={resultPhenotypes} genotypes={genotypes} />;
  } else if (size(recommendationAnnotations) > 0) {
    const multipopulation = size(uniq(map(recommendationAnnotations, (a) => a.population))) > 1;
    recommendationsToShow = map(recommendationAnnotations, (r) => <RecommendationAnnotation key={r.id} phenotypes={resultPhenotypes} genotypes={genotypes} multipopulation={multipopulation} {...r} />);
    recommendationsToShow.push(<Link href={`/genotypeResults?q=${encodeURIComponent(JSON.stringify(query))}`}>More annotations at the GSI</Link>);
  }

  return (
    <div className="guidelinePickerResultSection">
      {recommendationsToShow}
      <div className="mt-2"><Button actionHandler={onClear}>Change Genotype</Button></div>
    </div>
  );
}

function GeneralWarning() {
  return (
    <p className="font-weight-bold">
      <FontAwesomeIcon icon={['far', 'triangle-exclamation']} className="mr-1 text-warning" />
      Verify all entered information and recommendations that are retrieved before implementing in a healthcare setting
    </p>
  );
}

function DpwgWarning({source}) {
  if (source === 'DPWG') {
    return (
      <p>
        <InfoLink href="/page/dpwgMapping">Important information about alleles in DPWG guidelines.</InfoLink>
      </p>
    );
  } else {
    return null;
  }
}
